import { forwardRef, useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { createFilterOptions, FilterOptionsState, MultiAutocomplete, MultiAutocompleteProps } from 'ui';

const SELECT_ALL_OPTION_VALUE = 'select-all-value';

export type MultiAutocompleteOption = {
  value: number;
  label: string;
};

export type MultiAutocompleteFieldProps<T> = MultiAutocompleteProps<T> & {
  label: string;
  name: string;
  valueField?: keyof T;
  onOptionsChange?: (options: T[]) => void;
  withSelectAllOption?: boolean;
};

const getSelectedValue = <T extends MultiAutocompleteOption>(
  field: ReturnType<typeof useController>['field'],
  options: readonly T[],
  valueField: keyof T
) => {
  if (!field.value) {
    return [];
  }
  if (field.value === SELECT_ALL_OPTION_VALUE) {
    return options;
  }
  if (field)
    return field.value.map((value: T) => {
      if (typeof value === 'object' && value !== null) {
        return value;
      }

      const option = options.find((opt) => opt[valueField] === value);
      return option || { [valueField]: value };
    });
};

const DEFAULT_FILTER_OPTIONS: <T>(options: T[], state: FilterOptionsState<T>) => T[] = createFilterOptions({
  ignoreAccents: true,
  ignoreCase: true,
  matchFrom: 'any',
  trim: true,
});

const Field = <T extends MultiAutocompleteOption>(
  {
    name,
    options,
    required = false,
    valueField = 'value',
    getOptionLabel,
    onOptionsChange,
    withSelectAllOption = false,
    filterOptions = DEFAULT_FILTER_OPTIONS,
    ...rest
  }: MultiAutocompleteFieldProps<T>,
  ref: React.ForwardedRef<HTMLInputElement>
) => {
  const { control } = useFormContext();
  const { field, fieldState } = useController({
    control,
    name,
    rules: {
      required: required ? 'This field is required' : undefined,
    },
  });
  const { error } = fieldState;

  const selectedValues = getSelectedValue(field, options, valueField);

  const mergedOptions = useMemo(() => {
    const prevSelectedValues = Array.isArray(selectedValues) ? selectedValues : [];
    const filteredOptions = options || [];

    const optionValues = new Set(filteredOptions.map((option) => option[valueField]));
    const additionalOptions = prevSelectedValues.filter((value) => !optionValues.has(value[valueField]));
    return [...filteredOptions, ...additionalOptions];
  }, [options, selectedValues, valueField]);

  const getOptionLabelWithSelectAll = (option: T) => {
    if (option[valueField] === SELECT_ALL_OPTION_VALUE) {
      return 'Select all';
    }
    return getOptionLabel(option);
  };

  return (
    <MultiAutocomplete<T>
      clearOnBlur={false}
      options={mergedOptions}
      required={required}
      value={selectedValues}
      onChange={(_event, newValues) => {
        if (!newValues) {
          return field.onChange(undefined);
        }

        if (newValues.find((value) => value[valueField] === SELECT_ALL_OPTION_VALUE)) {
          field.onChange(mergedOptions);
          onOptionsChange?.(mergedOptions);
          return;
        }

        field.onChange(newValues);
        onOptionsChange?.(newValues);
      }}
      ref={ref}
      name={name}
      error={error ? error.message : undefined}
      getOptionLabel={withSelectAllOption ? getOptionLabelWithSelectAll : getOptionLabel}
      filterOptions={(options, state) => {
        const filteredOptions = filterOptions(options, state);
        if (withSelectAllOption) {
          return [{ [valueField]: SELECT_ALL_OPTION_VALUE } as unknown as T, ...filteredOptions];
        }
        return filteredOptions;
      }}
      {...rest}
    />
  );
};

export const MultiAutocompleteField = forwardRef(Field) as <T>(props: MultiAutocompleteFieldProps<T>) => JSX.Element;
